import { ref, toRef, watch, nextTick, computed, watchEffect } from 'vue'
import { api } from '@/helpers/api'
import { useGoogleAPI } from '@/composables/utils/useGoogleAPI'
import { useValidation } from '@/composables/utils/useValidation'
import { useInviteThroughSearchSteps } from './useInviteThroughSearchSteps'

const load = ref(false)
const validation = ref(null)
const autocomplete = ref(null)
const artists = ref([])

const userData = ref({
    invite: {
        email: '',
        artists: []
    },
    search: {
        vibe: '',
        location: '',
        lat: '',
        lon: '',
        radius: ''
    },
    selectedArtists: []
})
const URLS = {
    xhr: {
        mutate: {
            inviteArtists: '/api/booking-manager/invite-artists',
            searchArtists: '/api/search/artists'
        }
    }
}
export const useInviteThroughSearch = (props, { emit }) => {
    const eventId = toRef(props, 'eventId')
    const booker = toRef(props, 'bookerId')
    const showModal = toRef(props, 'showModal')
    const show = ref(showModal.value)
    const clearSelection = ref(false)

    const { isValid } = useValidation()
    const { currentStep, updateStep } = useInviteThroughSearchSteps()
    const { apiLoaded, initialize, lat, lng, fullAddress, placesTriggered } = useGoogleAPI()

    watch(
        () => showModal.value,
        (showBaseModal) => {
            show.value = showBaseModal
        }
    )
    watch(
        () => show.value,
        (showBaseModal) => {
            emit('update:show-modal', showBaseModal)
        }
    )
    watchEffect(() => {
        const googleRef = autocomplete.value

        if (apiLoaded.value && ref) {
            initialize({
                mode: 'autocomplete',
                templateRef: googleRef,
                types: ['(cities)']
            })
        }
    })
    watchEffect(() => {
        if (placesTriggered.value) {
            const { address } = _formatGoogleAddress(fullAddress.value)
            userData.value.search.location = address
            userData.value.search.lat = lat
            userData.value.search.lon = lng
        }
    })
    function _formatGoogleAddress(address) {
        const addressArr = address.split(', ')

        const city = addressArr[0]
        const stateZip = addressArr[1]
        const state = stateZip.split(' ')[0]

        const fullAddress = city + ', ' + state

        return { address: fullAddress, city }
    }

    function toggleModal(state = false) {
        if (state) {
            show.value = state
        } else {
            _clearAllData()
            updateStep('initial')
            show.value = state
        }
    }

    function _clearAllData() {
        const clearKeys = Object.keys(userData.value).filter((key) => key !== 'id' && key !== 'name')
        clearKeys.forEach((key) => clearData(key))
    }
    function clearData(clear) {
        if (!clear) return

        Object.keys(userData.value[clear]).forEach((key) => {
            if (clear === 'selectedArtists') {
                clearSelection.value = true
                nextTick(() => {
                    userData.value.selectedArtists.splice(0)
                    clearSelection.value = false
                })
            } else if (key === 'artists') {
                userData.value[clear][key] = []
            } else {
                userData.value[clear][key] = ''
            }
        })
    }

    const actionBtns = computed(() => {
        switch (currentStep.value.identifier) {
            case 'initial':
                return {
                    text: [null, 'Send Booking Request'],
                    action: _inviteNext
                }
            case 'search':
                return {
                    text: ['Clear All', 'Apply'],
                    action: _searchNext
                }
            case 'results':
                return {
                    text: [
                        'Deselect All',
                        `Add ${userData.value.selectedArtists.length} Artist${
                            userData.value.selectedArtists.length > 1 ? 's' : ''
                        }`
                    ],
                    action: _resultsNext
                }
            case 'final':
                return {
                    text: [null, 'Close'],
                    action: toggleModal
                }
        }
    })

    async function _inviteNext() {
        if (await _inviteArtist()) {
            updateStep('final')
        }
    }
    async function _searchNext() {
        if (!isValid(validation.value)) return

        if (await searchArtists()) {
            updateStep('results')
        }
    }

    async function _resultsNext() {
        userData.value.selectedArtists.forEach((artist) => {
            if (!userData.value.invite.artists.some((a) => a.id === artist.id)) {
                userData.value.invite.artists.push(artist)
            }
        })

        userData.value.selectedArtists = []

        updateStep('initial')
    }

    async function searchArtists() {
        try {
            load.value = true

            const isFiltered = Boolean(
                userData.value.search.vibe ||
                    userData.value.search.location ||
                    userData.value.search.lat ||
                    userData.value.search.lon
            )
            const { data: search } = await api({
                method: 'get',
                url: _constructSearchURL(!isFiltered)
            })
            const extractArtists = (result) => result.flatMap((item) => item.artists || [])

            const searchArtists = extractArtists(search.result)

            if (isFiltered) {
                artists.value = searchArtists
            } else {
                const { data: full } = await api({
                    method: 'get',
                    url: _constructSearchURL(true)
                })
                const fullArtists = extractArtists(full.result)
                const allArtists = [...searchArtists, ...fullArtists]

                artists.value = allArtists.filter(
                    (artist, index, self) => self.findIndex((a) => a.id === artist.id) === index
                )
            }

            return true
        } catch (error) {
            return false
        } finally {
            load.value = false
        }
    }
    function _constructSearchURL(full = false) {
        const { vibe, location, lat, lon } = userData.value.search

        const safeLocation = location || ''
        const locationParam = encodeURIComponent(safeLocation || '')
        const cityParam = encodeURIComponent(safeLocation.split(', ')[0] || '')
        const latParam = lat || ''
        const lonParam = lon || ''
        const vibeParam = encodeURIComponent(vibe || '')

        if (full) {
            return `${URLS.xhr.mutate.searchArtists}?location=&city=&lat=&lon=&search=&date=`
        }

        return `${URLS.xhr.mutate.searchArtists}?location=${locationParam}&city=${cityParam}&lat=${latParam}&lon=${lonParam}&search=${vibeParam}&date=`
    }
    function toggleArtist(artist) {
        let updatedArtists = [...userData.value.selectedArtists]

        const existingIndex = updatedArtists.findIndex((a) => a.id === artist.id)

        if (existingIndex === -1) {
            updatedArtists.push({
                id: artist.id,
                user_id: artist.user_id,
                name: artist.band_name
            })
        } else {
            updatedArtists.splice(existingIndex, 1)
        }

        userData.value.selectedArtists = updatedArtists
    }

    async function _inviteArtist() {
        try {
            load.value = true
            const artistUserIds = userData.value.invite.artists.filter((a) => a.user_id).map((a) => a.user_id)
            const artistUserEmails = userData.value.invite.artists
                .filter((a) => a.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email))
                .map((a) => a.email.trim())

            const additionalRecipients = [...new Set([...artistUserIds, ...artistUserEmails])]
            console.log('Sending user IDs:', additionalRecipients)
            await api({
                method: 'post',
                url: `${URLS.xhr.mutate.inviteArtists}/${booker.value}`,
                data: {
                    relevant_artist: [],
                    additional_recipients: additionalRecipients.join(', '),
                    event_id: eventId.value
                }
            })

            return true
        } catch {
            return false
        } finally {
            load.value = false
        }
    }

    const filterCount = computed(() => {
        let count = 0

        if (userData.value.search.location && userData.value.search.lat && userData.value.search.lon) {
            count++
        }
        if (userData.value.search.vibe) {
            count++
        }
        if (userData.value.search.radius) {
            count++
        }

        return count
    })
    return {
        load,
        show,
        artists,
        clearSelection,
        autocomplete,
        currentStep,
        updateStep,

        toggleModal,
        actionBtns,
        validation,
        userData,
        clearData,
        searchArtists,
        filterCount,
        toggleArtist
    }
}
